import { useState, useEffect, useRef } from 'react';
import { Search, X, User, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { effectiveFirstName, effectiveLastName, effectiveEmail } from '../../lib/personPrecedence';

interface PersonOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  position: string | null;
}

interface PersonSearchProps {
  value: PersonOption | null;
  onChange: (person: PersonOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Person search/select component with typeahead
 */
export const PersonSearch = ({
  value,
  onChange,
  placeholder = 'Search for a person...',
  disabled = false,
}: PersonSearchProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Search for people
  const searchPeople = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/api/people?search=${encodeURIComponent(searchQuery)}&limit=10`);
      // Map to display PersonOption using effective* helpers — rep overrides
      // win over Blast canonical for Pattern C parallel fields.
      setResults(
        response.data.people.map((p: any) => ({
          id: p.id,
          firstName: effectiveFirstName(p),
          lastName: effectiveLastName(p),
          email: effectiveEmail(p),
          position: p.position,
        }))
      );
    } catch (error) {
      console.error('Failed to search people:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchPeople(query);
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selection
  const handleSelect = (person: PersonOption) => {
    onChange(person);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  // Handle clear
  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  // If a person is selected, show their info
  if (value) {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-gray-500 dark:text-gray-300" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {value.firstName} {value.lastName}
            </p>
            {value.position && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{value.position}</p>
            )}
          </div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="input pl-10"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 && !isLoading && query.length >= 2 && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No people found</p>
          )}
          {results.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => handleSelect(person)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors"
            >
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {person.firstName} {person.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {person.position || person.email || 'No details'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
